import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./ExpenseCategories.module.css";
import { useNavigate } from "react-router";
import axios from "axios";
import { observer } from "mobx-react";
import config from "../../config/config";
import store from "../../Store/Store";
import { jwtDecode } from "jwt-decode";
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import {
  DataGrid,
  useGridApiRef,
  GridPreferencePanelsValue,
  getGridStringOperators,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid";
import AddNewCategory from "./AddNewCategory/AddNewCategory";
import CategoryNotification from "./CategoryNotification/CategoryNotification";
import { useDebouncedLog } from "../../useDebouncedLog";

/* ------------------------------------------------------------------
 * Grid sizing.
 * The grid is no longer autoHeight: autoHeight renders every row and
 * ignores the container, which pushed the pagination footer below the
 * fold. Instead the wrapper gets a definite height that grows with the
 * rows on the page and stops at 90vh — past that the grid scrolls its
 * own body and the toolbar/header/footer stay put.
 * ------------------------------------------------------------------ */
const GRID_ROW_HEIGHT = 64;
const GRID_HEADER_HEIGHT = 56; // default columnHeaderHeight
const GRID_FOOTER_HEIGHT = 53; // pagination row
// this grid passes showToolbar — drop this term if that prop ever goes
const GRID_TOOLBAR_HEIGHT = 54;
const GRID_MAX_HEIGHT = "90vh";
// rows to reserve space for when the grid is empty, so the no-rows /
// all-columns-hidden overlays have room
const GRID_MIN_ROWS = 3;

/* ------------------------------------------------------------------
 * Column widths.
 * Fixed widths left a gap on the right whenever the container was wider
 * than the sum of the columns. The two control columns keep a fixed
 * width (their content never grows), and the three content columns take
 * whatever is left over via flex, in the proportions below. minWidth
 * keeps them readable on a narrow viewport — below that total the grid
 * scrolls horizontally instead of squeezing.
 * ------------------------------------------------------------------ */
const NAME_FLEX = 1;
const DESCRIPTION_FLEX = 1.8;
const GL_CODE_FLEX = 0.9;
const STATUS_WIDTH = 170;
const ACTION_WIDTH = 130;

// Text columns get exactly one operator, so the operator dropdown can be
// hidden and the panel shows nothing but a text box.
const CONTAINS_ONLY = getGridStringOperators().filter(
  (operator) => operator.value === "contains",
);

// Single-select columns get "is", which renders a value dropdown instead.
const IS_ONLY = getGridSingleSelectOperators().filter(
  (operator) => operator.value === "is",
);

// Same parser the built-in GridToolbarQuickFilter uses.
const parseQuickFilter = (input) =>
  input.split(" ").filter((word) => word !== "");

const EMPTY_FILTER_MODEL = {
  items: [],
  quickFilterValues: [],
  // hidden columns stay searchable
  quickFilterExcludeHiddenColumns: false,
};

const ExpenseCategories = observer(({ className = "" }) => {
  const [refresh, setRefresh] = useState(true);
  const [editCategory, setEditCategory] = useState(false);
  const [openCategory, setOpenCategory] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [hoveredItem, setHoveredItem] = useState({
    software: null,
    index: null,
  });
  const [initialObj, setinitialObj] = useState();
  const [categoryUpdated, setCategoryUpdated] = useState(false);
  const [data, setData] = useState([]);

  // --- DataGrid state (all controls live in the toolbar row) ---
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterModel, setFilterModel] = useState(EMPTY_FILTER_MODEL);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [panelAnchorEl, setPanelAnchorEl] = useState(null);
  // Controlled so the component can tell when the user has hidden everything.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  // The columns panel always anchors here, whatever opened it.
  const columnsButtonRef = useRef(null);

  var logCategoryFeched;
  var logCategoryVisited;
  try {
    logCategoryVisited = useDebouncedLog("admin_category_visited", 500);
    logCategoryFeched = useDebouncedLog("admin_category_fetched", 500);
  } catch (err) {
    console.log("error", err);
  }

  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);

      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
        return decoded.exp < currentTime;
      }
    } catch (error) {
      console.log("Error decoding token:", error);
      return true; // Treat decoding failures as expired tokens
    }
    return true;
  };

  const getNewTokens = async (authData) => {
    if (!authData?.RefreshToken) {
      localStorage.removeItem("authToken");
      // navigate("/login");
      return;
    }

    try {
      const client = new CognitoIdentityProviderClient({
        region: "ap-south-1",
      });

      const params = {
        ClientId: config.Client_Id,
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: {
          REFRESH_TOKEN: authData?.RefreshToken,
        },
      };

      const command = new InitiateAuthCommand(params);
      const refreshResponse = await client.send(command);

      if (refreshResponse.AuthenticationResult) {
        const newAuthToken = {
          AccessToken: refreshResponse.AuthenticationResult.AccessToken,
          RefreshToken: authData?.RefreshToken, // Keep old refresh token
          IdToken: refreshResponse.AuthenticationResult.IdToken,
        };

        localStorage.setItem("authToken", JSON.stringify(newAuthToken));
        setAuth(newAuthToken); // Update state
        if (store.refresh) {
          store.setRefresh(false);
        } else {
          store.setRefresh(true);
        }
        return newAuthToken;
      } else {
        localStorage.removeItem("authToken");
        // navigate("/login");
      }
    } catch (error) {
      console.log("Error refreshing tokens:", error);
      localStorage.removeItem("authToken");
      // navigate("/login");
    }
  };

  const getCategories = async () => {
    try {
      let tokens = localStorage.getItem("authToken");
      let parsedTokens = JSON.parse(tokens);
      const Obj = {
        ledger_label: store.Company_Id,
      };
      const expired = isTokenExpired(parsedTokens.IdToken);
      if (expired) {
        parsedTokens = getNewTokens(parsedTokens);
      }
      let payload = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${config.node_api_endpoint}/cupi/admin/get_categories`,
        headers: {
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
          "Content-Type": "application/json",
        },
        data: Obj,
      };
      axios
        .request(payload)
        .then((res) => {
          setData(res?.data?.data);
          try {
            let intialData = res?.data?.data;
            let final_obj = {};
            for (let i = 0; i < intialData.length; i++) {
              final_obj[intialData[i].categoryId] = intialData[i].isActive;
            }
            setinitialObj(final_obj);
          } catch (err) {
            console.log("get category", err);
          }
          try {
            logCategoryFeched;
          } catch (err) {
            console.log("error", err);
          }
        })
        .catch((err) => {
          console.log("Error %o", err);
        });
    } catch (err) {
      console.log("error %o", err);
    }
  };

  useEffect(() => {
    getCategories();
  }, [store.refresh]);

  useEffect(() => {
    logCategoryVisited();
  }, [logCategoryVisited]);

  /* ----------------------------------------------------------------
   * Search -> MUI quick filter.
   * The grid owns the filtering; this just hands it the parsed values,
   * debounced the way the built-in quick filter does.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const timeout = setTimeout(() => {
      apiRef.current?.setQuickFilterValues(parseQuickFilter(searchText.trim()));
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchText]);

  const resetGridFilters = () => {
    setSearchText("");
    setSearchOpen(false);
    setFilterModel(EMPTY_FILTER_MODEL);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const SANSOURCE = "var( --font-source-sans-3), sans-serif";

// Body / cell text colour, matching --color-dimgray-300.
const HEADER_COLOR = "#666666";
const TEXT_COLOR = "#4C4E64";
// Accent used for checked states and the panel's text buttons.
const ACCENT_COLOR = "#3246A9";
const MUTED_COLOR = "#8C8C8C";

  const SELECT_MENU_SX = {
    borderRadius: "var(--br-xs)",
    "& .MuiMenuItem-root": {
      fontFamily: SANSOURCE,
      fontSize: 14,
      fontWeight: 500,
      color: TEXT_COLOR,
      letterSpacing: 0,
      textTransform: "none",
    },
    "& .MuiMenuItem-root.Mui-selected": {
      color: ACCENT_COLOR,
      backgroundColor: "rgba(50, 70, 169, 0.08)",
    },
    "& .MuiMenuItem-root.Mui-selected:hover": {
      backgroundColor: "rgba(50, 70, 169, 0.12)",
    },
  };
const PANEL_SX = {
  // catch-all for nodes MUI doesn't expose a stable class for
  "& , & *": {
    fontFamily: SANSOURCE,
  },
  "& .MuiDataGrid-panelWrapper": {
    fontFamily: SANSOURCE,
  },
  // column rows: "DATE", "PAID TO", ...
  "& .MuiFormControlLabel-label, & .MuiTypography-root": {
    fontFamily: SANSOURCE,
    fontSize: 14,
    fontWeight: 500,
    color: TEXT_COLOR,
    letterSpacing: 0,
    textTransform: "none",
  },
  // the panel's own search box and the filter form inputs
  "& .MuiInputBase-root, & .MuiInputBase-input, & .MuiFormLabel-root, & .MuiSelect-select":
    {
      fontFamily: SANSOURCE,
      fontSize: 14,
      color: TEXT_COLOR,
    },
  "& .MuiInputBase-input::placeholder": {
    fontFamily: SANSOURCE,
    color: MUTED_COLOR,
    opacity: 1,
  },
  // Show/Hide All + RESET footer
  "& .MuiDataGrid-panelFooter .MuiButton-root": {
    fontFamily: SANSOURCE,
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    color: ACCENT_COLOR,
  },
  // checkboxes, otherwise MUI's default blue
  "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate":
    {
      color: ACCENT_COLOR,
    },
     "& .MuiPaper-root, & .MuiDataGrid-paper": {
    borderRadius: "var(--br-xs)",   // 12px, matches your inputs
    overflow: "hidden",             // clips children to the radius
  },
  "& .MuiDataGrid-panelWrapper": {
    borderRadius: "var(--br-xs)",
    overflow: "hidden",
  },
};

  const toggleSearch = () => {
    setSearchOpen((open) => {
      if (open) {
        setSearchText(""); // closing clears the quick filter
      }
      return !open;
    });
  };

  const openFilterPanel = (event) => {
    setPanelAnchorEl(event.currentTarget);
    apiRef.current?.showFilterPanel();
  };

  const openColumnsPanel = (event) => {
    // Pinned to the toolbar icon so the panel lands in the same place whether
    // it was opened from the toolbar or from the all-columns-hidden state.
    setPanelAnchorEl(columnsButtonRef.current ?? event?.currentTarget ?? null);
    apiRef.current?.showPreferences(GridPreferencePanelsValue.columns);
  };

  const setCategories = async () => {
    try {
      let tokens = localStorage.getItem("authToken");
      let parsedTokens = JSON.parse(tokens);

      for (let i = 0; i < data.length; i++) {
        if (initialObj[data[i].categoryId] != data[i].isActive) {
          console.log("data didnot match update", data[i].categoryName);
          const obj = {
            ledger_label: store.Company_Id,
            category_id: data[i].categoryId,
            is_active: data[i].isActive,
          };
          console.log(obj);
          let payload = {
            method: "post",
            maxBodyLength: Infinity,
            url: `${config.node_api_endpoint}/cupi/admin/update_category_status`,
            headers: {
              aws_cognito_id_token: parsedTokens.IdToken,
              aws_cognito_access_token: parsedTokens.AccessToken,
              "Content-Type": "application/json",
            },
            data: obj,
          };
          axios
            .request(payload)
            .then(async (res) => {
              console.log(res.data);
              setCategoryUpdated(true);
              await new Promise((resolve) => setTimeout(resolve, 3000));
              setCategoryUpdated(false);
            })
            .catch((err) => { });
        }
      }
    } catch (err) {
      console.log("error %o", err);
    }
  };

  // Helper function to format keys
  const formatKey = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  };

  const toggleActive = (categoryId) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.categoryId === categoryId
          ? { ...item, isActive: !item.isActive }
          : item,
      ),
    );
  };

  /* ----------------------------------------------------------------
   * Grid rows.
   * Display values are precomputed into their own fields so the filter
   * panel and the search box operate on what the user actually sees.
   * ---------------------------------------------------------------- */
  const gridRows = useMemo(
    () =>
      (Array.isArray(data) ? data : []).map((detail, index) => ({
        ...detail,
        id: `${detail.categoryId ?? "row"}-${index}`,
        __description: detail.description ? detail.description : "-",
        __glCode: detail.gl_code ? detail.gl_code : "NA",
        __status: detail.isActive ? "Enabled" : "Disabled",
      })),
    [data],
  );

  const statusOptions = useMemo(
    () => [...new Set(gridRows.map((row) => row.__status))].sort(),
    [gridRows],
  );

  const ellipsisCell = (value) => (
    <Tooltip title={value || ""}>
      <span
        style={{
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </Tooltip>
  );

  const isReadOnly = store.isUserViewAdmin || store.isUserGlobalViewAdmin;

  const columns = useMemo(() => {
    const cols = [
      {
        field: "categoryName",
        headerName: "NAME",
        // content columns share the leftover width instead of sitting at a
        // fixed size and leaving a gap on the right
        flex: NAME_FLEX,
        minWidth: 180,
        // hideable: false,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__description",
        headerName: "DESCRIPTION",
        flex: DESCRIPTION_FLEX,
        minWidth: 240,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__glCode",
        headerName: "GL-CODE",
        flex: GL_CODE_FLEX,
        minWidth: 140,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__status",
        headerName: "ENABLE/DISABLE",
        // fixed: the toggle is always the same size
        width: STATUS_WIDTH,
        headerAlign: "center",
        align: "center",
        type: "singleSelect",
        valueOptions: statusOptions,
        filterOperators: IS_ONLY,
        renderCell: (params) => {
          const detail = params.row;
          if (
            detail.categoryName === "Uncategorized" ||
            detail.categoryName === "Other"
          ) {
            return null;
          }
          return (
            <div className={styles.button}>
              {!isReadOnly ? (
                <button
                  className={styles.button1}
                  onClick={() => toggleActive(detail.categoryId)}
                >
                  <img
                    alt=""
                    src={detail.isActive ? "/Track(Active).svg" : "/Track.svg"}
                    className={styles.btn_img}
                  />
                </button>
              ) : (
                <button className={styles.button22}>
                  <img
                    alt=""
                    src={detail.isActive ? "/Track(Active).svg" : "/Track.svg"}
                    className={styles.btn_img22}
                  />
                </button>
              )}
            </div>
          );
        },
      },
    ];

    if (!isReadOnly) {
      cols.push({
        field: "__action",
        headerName: "ACTION",
        // fixed: one button, never needs more room
        width: ACTION_WIDTH,
        sortable: false,
        filterable: false,
        // hideable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <button
            className={styles.button22}
            onClick={() => {
              store.setCategoryInfo(params.row);
              setOpenCategory(true);
              setEditCategory(true);
            }}
          >
            <img alt="" src="/edit-02.svg" className={styles.btn_img22} />
            Edit
          </button>
        ),
      });
    }

    return cols;
  }, [statusOptions, isReadOnly]);

  /* ----------------------------------------------------------------
   * The grid has no "no visible columns" state of its own: hide every
   * column and it renders an empty shell with no overlay. Detect it here
   * and put a Manage Columns action in that space instead.
   * ---------------------------------------------------------------- */
  const allColumnsHidden = useMemo(
    () => columns.every((col) => columnVisibilityModel[col.field] === false),
    [columns, columnVisibilityModel],
  );

  /* ----------------------------------------------------------------
   * Wrapper height: grows with the rows on the current page, capped at
   * 90vh. gridRows is the pre-pagination row set, so this is an upper
   * bound — a page filtered down to fewer rows simply leaves a little
   * empty space, it never clips the footer.
   * ---------------------------------------------------------------- */
  const rowsOnPage =
    Math.min(gridRows.length, paginationModel.pageSize) || GRID_MIN_ROWS;
  const gridHeight = `min(${GRID_MAX_HEIGHT}, ${GRID_TOOLBAR_HEIGHT +
    GRID_HEADER_HEIGHT +
    GRID_FOOTER_HEIGHT +
    rowsOnPage * GRID_ROW_HEIGHT
    }px)`;

  return (
    <div className={styles.pageContainer + " " + className}>
      <div className={styles.frameParent}>
        <div className={styles.frameGroup}>
          {!store.isUserViewAdmin && !store.isUserGlobalViewAdmin && (
            <div className={styles.AddCategorybutton}>
              <button
                className={styles.AddCategorybuttonBtn}
                onClick={() => {
                  setOpenCategory(true);
                }}
              >
                <img
                  className={styles.escrowLogo}
                  alt=""
                  src="/pluscircle.svg"
                />
                Add a Category
              </button>
            </div>
          )}
          <div
            className={styles.feeStatementDataTable}
            style={{ minWidth: 0, maxWidth: "100%" }}
          >
            <div className={styles.top}>
              <div className={styles.filterWrapper}>
                <div className={styles.filter}>
                  <div className={styles.containertext}>
                    Expense Categories List
                  </div>
                </div>
                {categoryUpdated && (
                  <div className={styles.reLoadTablepopup}>
                    <div className={styles.reLoadcontainer}>
                      Expense categories has been updated.
                    </div>
                  </div>
                )}
                <div className={styles.filter}>
                  {/* search — hidden until the search button is pressed */}
                  {searchOpen && (
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") toggleSearch();
                      }}
                      sx={{
                        minWidth: 220,
                        "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                      }}
                    />
                  )}

                  {/* search toggle */}
                  <button className={styles.filterLinesParent} onClick={toggleSearch}>
                    <img
                      className={styles.filterLinesIcon}
                      alt=""
                      src="/SearchSVG.svg"
                    />
                  </button>

                  <button
                    ref={columnsButtonRef}
                    className={styles.filterLinesParent}
                    onClick={openColumnsPanel}
                  >
                    <img
                      className={styles.filterLinesIcon}
                      alt=""
                      src="/ThreeHorizontalLine.svg"
                    />
                  </button>

                  {/* column filter — column + value, no operator */}
                  <button
                    className={styles.filterLinesParent}
                    onClick={openFilterPanel}
                  >
                    <img
                      className={styles.filterLinesIcon}
                      alt=""
                      src="/ThreeVerticalline.svg"
                    />
                  </button>

                  {/* show / hide columns */}

                  {/* apply — unchanged */}
                  {!store.isUserViewAdmin && !store.isUserGlobalViewAdmin ? (
                    <button
                      className={styles.reLoadTable}
                      onClick={async () => {
                        //DONT ALLOW VIEW ADMIN
                        setCategories();
                        if (refresh) {
                          setRefresh(false);
                          return;
                        }
                        setRefresh(true);
                      }}
                    >
                      <div className={styles.reLoadTabletext}>Apply</div>
                    </button>
                  ) : (
                    <button className={styles.reLoadTableinactive}>
                      <div className={styles.reLoadTabletext}>Apply</div>
                    </button>
                  )}
                </div>
              </div>
              <div className={styles.topChild} />
            </div>

            <Box
              sx={{
                position: "relative",
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                // definite height, capped at 90vh — the grid scrolls its own
                // rows, so the header and the pagination footer stay visible
                height: gridHeight,
                ...(allColumnsHidden && { minHeight: 280 }),
                overflow: "hidden",
              }}
            >
              <DataGrid
                apiRef={apiRef}
                rows={gridRows}
                columns={columns}
                rowHeight={GRID_ROW_HEIGHT}
                columnHeaderHeight={GRID_HEADER_HEIGHT}
                disableRowSelectionOnClick
                filterModel={filterModel}
                onFilterModelChange={(model) => {
                  setFilterModel(model);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                showToolbar
                slotProps={{
                   baseSelect: {
                    MenuProps: { PaperProps: { sx: SELECT_MENU_SX } },
                  },
                  panel: {
                    anchorEl: panelAnchorEl,
                    placement: "bottom-end",
                    sx: PANEL_SX
                  },
                  filterPanel: {
                    // one operator per column, so the selector is noise
                    filterFormProps: {
                      logicOperatorInputProps: { sx: { display: "none" } },
                      operatorInputProps: { sx: { display: "none" } },
                      columnInputProps: { sx: { minWidth: 160 } },
                      valueInputProps: {
                        InputComponentProps: { sx: { minWidth: 220 } },
                      },
                    },
                    sx: {
                      "& .MuiDataGrid-filterFormLogicOperatorInput": {
                        display: "none",
                      },
                      "& .MuiDataGrid-panelWrapper": { minWidth: 460 },
                      "& .MuiDataGrid-panelContent": { overflowX: "hidden" },
                      "& .MuiDataGrid-filterForm": {
                        p: 2,
                        gap: 1,
                        alignItems: "flex-end",
                        flexWrap: "wrap",
                      },
                      "& .MuiDataGrid-filterFormDeleteIcon": {
                        flexShrink: 0,
                        alignSelf: "flex-end",
                      },
                    },
                  },
                }}
                localeText={{
                  noRowsLabel: "No categories found.",
                  noResultsOverlayLabel:
                    "No categories match this search or filter.",
                  filterPanelColumns: "Column",
                  filterPanelInputLabel: "Value",
                  filterPanelInputPlaceholder: "Filter value",
                }}
                sx={{
                  // fills the capped wrapper; the body scrolls, the footer doesn't
                  height: "100%",
                  width: "100%",
                  maxWidth: "100%",
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f7f8fa",
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: 0.4,
                  },
                  "& .MuiDataGrid-columnHeader": {
                    paddingLeft: "10px",
                    paddingRight: 0,
                  },
                  "& .MuiDataGrid-columnSeparator": { display: "none" },
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "none",
                    overflow: "hidden",
                    paddingLeft: "10px",
                    paddingRight: 0,
                  },
                  "& .MuiDataGrid-footerContainer": { borderTop: "none" },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                  {
                    outline: "none",
                  },
                }}
              />

              {/* every column hidden — the grid draws nothing, so offer the
                  way back instead of a blank table */}
              {allColumnsHidden && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    backgroundColor: "#fff",
                    zIndex: 2,
                  }}
                >
                  <span style={{ fontSize: 14, color: "#667085" }}>
                    All columns are hidden.
                  </span>
                  <button
                    className={styles.reLoadTable1}
                    onClick={openColumnsPanel}
                  >
                    <div className={styles.reLoadTabletext1}>Manage Columns</div>
                  </button>
                </div>
              )}
            </Box>
          </div>
        </div>
      </div>
      {openCategory && (
        <div className={styles.newUsersOverlay1}>
          <AddNewCategory
            isVisible={openCategory}
            isEditCategory={editCategory}
            setClose={() => {
              setOpenCategory(false);
              setEditCategory(false);
            }}
            NewCategoryAdded={() => {
              setOpenNotification(true);
              setEditCategory(false);
              store.refresh ? store.setRefresh(false) : store.setRefresh(true);
            }}
          />
        </div>
      )}
      {openNotification && (
        <div className={styles.newUsersOverlay2}>
          <CategoryNotification
            isVisible={openNotification}
            setClose={() => {
              setOpenNotification(false);
            }}
          />
        </div>
      )}
    </div>
  );
});

ExpenseCategories.propTypes = {
  className: PropTypes.string,
};

export default ExpenseCategories;
